import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSubmitIntake } from '../hooks/mutations/useSubmitIntake';
import { useAccessToken } from '../hooks/useProfile';
import { Button } from './ui/button';
import { LoaderCircle, Sparkles } from 'lucide-react';
import {
  WorkflowError,
  workflowActionsClass,
  workflowFieldClass,
  workflowPrimaryClass,
  workflowSecondaryClass,
  workflowSurfaceClass,
} from './WorkflowForm';
import {
  SURVEY_FIELDS,
  SURVEY_DEFAULT_VALUES,
  SURVEY_GROUPS,
  type SurveyField,
} from '../survey-questions';

interface RacePlan {
  id?: number;
  name?: string;
  email?: string;
  race_name?: string;
  race_date?: number | string;
  race_distance?: string;
  race_gpx_file_url?: string;
  goal_time?: string;
  survey_id?: string;
  survey_responses?: string;
  created_at?: string;
  updated_at?: string;
}

interface IntakeFormProps {
  data?: RacePlan;
  onSuccess?: () => void;
  user?: { name?: string; email?: string };
}

interface RaceSuggestion {
  id: number;
  name: string;
  date: string;
}

const RaceNameTypeahead = ({
  raceDate,
  fieldProps,
  onRaceIdChange,
}: {
  raceDate: string;
  fieldProps: any;
  onRaceIdChange: (raceId: number | '') => void;
}) => {
  const { data: accessToken } = useAccessToken();
  const [isOpen, setIsOpen] = useState(false);
  const { data: races = [], isFetching } = useQuery<RaceSuggestion[]>({
    queryKey: ['race-suggestions', raceDate],
    queryFn: async () => {
      const url = new URL(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races`,
      );
      url.searchParams.set('startDate', raceDate);
      url.searchParams.set('endDate', raceDate);
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Unable to look up races');
      const data = await response.json();
      return data.races || [];
    },
    enabled: Boolean(raceDate && accessToken),
  });
  const value = String(fieldProps.state.value || '');
  const matches = races.filter((race) =>
    race.name.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
  );

  return (
    <div className="relative">
      <input
        type="text"
        id="race_name"
        value={value}
        onFocus={() => setIsOpen(true)}
        onChange={(event) => {
          const nextValue = event.target.value;
          fieldProps.handleChange(nextValue);
          const exactMatch = races.find(
            (race) =>
              race.name.toLocaleLowerCase() === nextValue.toLocaleLowerCase(),
          );
          onRaceIdChange(exactMatch?.id ?? '');
          setIsOpen(true);
        }}
        onBlur={() => {
          fieldProps.handleBlur();
          window.setTimeout(() => setIsOpen(false), 150);
        }}
        placeholder={
          raceDate ? 'Start typing a race name' : 'Choose a race date first'
        }
        autoComplete="off"
        disabled={!raceDate}
        aria-autocomplete="list"
        aria-controls="race-name-suggestions"
        aria-expanded={isOpen && Boolean(raceDate)}
        className="block w-full min-w-0 max-w-full box-border p-3.5 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-brand-volt focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
      />
      {isOpen && raceDate && value && matches.length > 0 ? (
        <ul
          id="race-name-suggestions"
          role="listbox"
          className="absolute z-10 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {matches.map((race) => (
            <li key={race.id} role="option" aria-selected={false}>
              <button
                type="button"
                className="w-full px-4 py-3 text-left text-sm hover:bg-brand-offWhite focus:bg-brand-offWhite"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  fieldProps.handleChange(race.name);
                  onRaceIdChange(race.id);
                  setIsOpen(false);
                }}
              >
                {race.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {raceDate && isFetching ? (
        <p className="mt-1 text-xs text-gray-500">Looking up races…</p>
      ) : null}
      {raceDate && value && !isFetching && matches.length === 0 ? (
        <p className="mt-1 text-xs text-gray-500">
          No matching race found. Your custom race name will be used.
        </p>
      ) : null}
    </div>
  );
};

const renderField = (
  field: SurveyField,
  fieldProps: any,
  onValueChange?: (value: string | number) => void,
) => {
  if (field.type === 'select') {
    return (
      <select
        id={field.name}
        value={fieldProps.state.value as string}
        onChange={(e) => fieldProps.handleChange(e.target.value)}
        onBlur={fieldProps.handleBlur}
        className={workflowFieldClass}
      >
        {field.options?.map((option) => {
          if (typeof option === 'string') {
            return (
              <option key={option} value={option}>
                {option}
              </option>
            );
          }
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        id={field.name}
        value={fieldProps.state.value as string}
        onChange={(e) => fieldProps.handleChange(e.target.value)}
        onBlur={fieldProps.handleBlur}
        placeholder={field.placeholder}
        rows={field.rows || 3}
        className={`${workflowFieldClass} min-h-28 resize-y`}
      />
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center">
        <input
          type="checkbox"
          id={field.name}
          checked={fieldProps.state.value as boolean}
          onChange={(e) => fieldProps.handleChange(e.target.checked)}
          onBlur={fieldProps.handleBlur}
          className="h-5 w-5 text-brand-cobalt rounded"
        />
        <label htmlFor={field.name} className="ml-2 text-sm">
          {field.question}
        </label>
      </div>
    );
  }

  return (
    <input
      type={field.type}
      id={field.name}
      value={fieldProps.state.value as string}
      onChange={(e) => {
        const value =
          field.type === 'number'
            ? e.target.value === ''
              ? ''
              : Number(e.target.value)
            : e.target.value;
        fieldProps.handleChange(value);
        onValueChange?.(value);
      }}
      onBlur={fieldProps.handleBlur}
      placeholder={field.placeholder}
      className={`${workflowFieldClass} min-w-0 max-w-full appearance-none`}
    />
  );
};

const GeneratingState = ({ raceName }: { raceName?: string }) => (
  <div className="surface-card mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center p-8 text-center sm:p-12">
    <div className="relative mb-8">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-midnight text-brand-volt">
        <Sparkles className="h-8 w-8" />
      </div>
    </div>
    <h2 className="text-xl font-bold text-gray-900 mb-3">
      Generating Your Training Plan
    </h2>
    <p className="text-sm text-gray-600 mb-2">
      {raceName
        ? `Building your personalized plan for ${raceName}`
        : 'Building your personalized training plan'}
    </p>
    <p className="text-xs text-gray-400">
      This usually takes 30-60 seconds. The page will update automatically when
      your plan is ready.
    </p>
    <LoaderCircle className="mt-6 h-5 w-5 animate-spin text-brand-cobalt" />
  </div>
);

const IntakeForm = ({ data }: IntakeFormProps) => {
  const [error, setError] = useState<string | null>(null);
  const [groupErrors, setGroupErrors] = useState<Record<string, string[]>>({});
  const [submitted, setSubmitted] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(0);

  const isReadOnly = !!data?.id;

  const submitIntake = useSubmitIntake({
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (err) => {
      setError(err.message || 'An error occurred while submitting the form');
    },
  });

  const form = useForm({
    defaultValues: {
      ...SURVEY_DEFAULT_VALUES,
      race_id: '',
    } as Record<string, string | number | boolean>,
    onSubmit: async ({ value }) => {
      if (isReadOnly) return;

      submitIntake.mutate(value);
    },
  });

  if (submitted) {
    return <GeneratingState />;
  }

  if (isReadOnly) {
    return <GeneratingState raceName={data?.race_name} />;
  }

  const groupFields = SURVEY_FIELDS.filter(
    (f) => f.group === SURVEY_GROUPS[currentGroup].id,
  );
  const isLastGroup = currentGroup === SURVEY_GROUPS.length - 1;

  const validateCurrentGroup = () => {
    const values = form.state.values as Record<string, any>;
    const errors = groupFields.flatMap((field) => {
      const message = field.validators?.onSubmit?.(values[field.name]);
      return message ? [`${field.question}: ${message}`] : [];
    });
    setGroupErrors((current) => ({
      ...current,
      [SURVEY_GROUPS[currentGroup].id]: errors,
    }));
    if (errors.length > 0) {
      setError(`Complete the required fields before continuing.`);
      return false;
    }
    setError(null);
    return true;
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-1 pb-6 pt-3 sm:px-0 sm:pt-5">
      <div className="mb-5 flex justify-end px-1 sm:px-0">
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-brand-slate">Race plan setup</span>
      </div>

      {/* Progress bar */}
      <div className="mb-7 grid grid-cols-5 gap-2" aria-label="Race plan setup progress">
        {SURVEY_GROUPS.map((g, i) => (
          <div key={g.id} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={`h-1.5 w-full rounded-full transition-colors ${i < currentGroup ? 'bg-brand-cobalt' : i === currentGroup ? 'bg-brand-volt' : 'bg-gray-200 dark:bg-slate-700'}`}
            />
            <span
              className={`text-[10px] transition-colors ${i === currentGroup ? 'font-bold text-brand-cobalt' : i < currentGroup ? 'font-medium text-brand-slate' : 'text-gray-400'}`}
              aria-current={i === currentGroup ? 'step' : undefined}
            >
              {groupErrors[g.id]?.length ? `${i + 1} ·` : i + 1}
            </span>
          </div>
        ))}
      </div>

      {/* Group header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {SURVEY_GROUPS[currentGroup].label}
        </h3>
        <p className="text-sm text-gray-500">
          {SURVEY_GROUPS[currentGroup].description}
        </p>
      </div>

      {error ? (
        <WorkflowError>{error}</WorkflowError>
      ) : null}
      {groupErrors[SURVEY_GROUPS[currentGroup].id]?.length ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <p className="font-semibold">There is more to complete on this step:</p>
          <ul className="mt-1 list-disc pl-5">
            {groupErrors[SURVEY_GROUPS[currentGroup].id].map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className={`${workflowSurfaceClass} space-y-5`}
      >
        {groupFields.map((field) => (
          <form.Field
            key={field.name}
            name={field.name as any}
            validators={field.validators}
            children={(fieldProps) => (
              <div className={field.type === 'date' ? 'max-w-[14rem]' : ''}>
                <label
                  htmlFor={field.name}
                  className="block mb-1.5 text-sm font-medium text-gray-700"
                >
                  {field.question}
                  {field.required ? (
                    <span className="text-red-500 ml-1">*</span>
                  ) : null}
                </label>
                {field.name === 'race_name' ? (
                  <form.Subscribe
                    selector={(state) => String(state.values.race_date || '')}
                    children={(raceDate) => (
                      <RaceNameTypeahead
                        raceDate={raceDate}
                        fieldProps={fieldProps}
                        onRaceIdChange={(raceId) =>
                          form.setFieldValue('race_id', raceId)
                        }
                      />
                    )}
                  />
                ) : (
                  renderField(field, fieldProps, () => {
                    if (field.name === 'race_date') {
                      form.setFieldValue('race_name', '');
                      form.setFieldValue('race_id', '');
                    }
                  })
                )}
                {fieldProps.state.meta.isTouched &&
                fieldProps.state.meta.errors.length > 0 ? (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldProps.state.meta.errors[0]}
                  </p>
                ) : null}
              </div>
            )}
          />
        ))}
      </form>

      <div className={`${workflowActionsClass} bg-background/80 px-1 pb-3 backdrop-blur sm:px-0`}>
        <div className="mx-auto flex max-w-4xl gap-3">
          {currentGroup > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentGroup((g) => g - 1)}
              className={`${workflowSecondaryClass} min-h-12 px-6 text-base`}
            >
              Back
            </Button>
          ) : null}
          {!isLastGroup ? (
            <Button
              type="button"
              onClick={() => {
                if (validateCurrentGroup()) setCurrentGroup((g) => g + 1);
              }}
              className={`${workflowPrimaryClass} min-h-12 text-base font-black`}
            >
              Next
            </Button>
          ) : (
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit]) => (
                <Button
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                  }}
                  disabled={!canSubmit || submitIntake.isPending}
                  className={`${workflowPrimaryClass} min-h-12 text-base font-black`}
                >
                  {submitIntake.isPending
                    ? 'Generating...'
                    : 'Create my race plan'}
                </Button>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeForm;
